import { useState, useRef, useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faCheck,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./SearchableDropdown.module.css";
import { dropdownManager } from "../../hooks/DropdownManager";
import { useLanguage } from "../../context/LanguageContext";

interface Option {
  label: string;
  value: string | null;
}

interface SearchableDropdownProps {
  label?: string;
  options: Option[];
  value: string | string[];
  multiple?: boolean;
  onChange: (value: any) => void;
  icon?: IconDefinition;
  disabled?: boolean;
  onSearch?: (text: string) => void;
  onOpen?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autoOpen?: boolean;
}

export default function SearchableDropdown({
  label,
  options,
  value,
  multiple = false,
  onChange,
  onSearch,
  onOpen,
  icon,
  disabled = false,
  onKeyDown,
  autoOpen = false,
}: SearchableDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [zIndex, setZIndex] = useState(10000);
  const dropdownId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen) {
      const newZIndex = dropdownManager.getNextZIndex(dropdownId);
      setZIndex(newZIndex);
      
      return () => {
        dropdownManager.releaseZIndex(dropdownId);
      };
    }
  }, [isOpen, dropdownId]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [search]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        closeDropdown();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen]);

  const getMenuPosition = () => {
    if (!buttonRef.current) return { display: 'none' };
    
    const rect = buttonRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const top = rect.bottom + scrollTop + 4;

    return {
      position: 'absolute' as const,
      top: `${top}px`,
      left: `${rect.left + scrollLeft}px`,
      width: `${rect.width}px`,
      zIndex: zIndex,
    };
  };

  const scrollIntoViewIfNeeded = () => {
    if (!buttonRef.current || !menuRef.current) return;

    setTimeout(() => {
      const buttonRect = buttonRef.current!.getBoundingClientRect();
      const menuMaxHeight = 300;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      
      if (spaceBelow < menuMaxHeight) {
        const scrollAmount = menuMaxHeight - spaceBelow + 20;
        window.scrollBy({
          top: scrollAmount,
          behavior: 'smooth'
        });
      }
    }, 50);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setSearch("");
    setHighlightedIndex(0);
    buttonRef.current?.focus();
  };

  const toggleOpen = () => {
    if (disabled) return;
    
    const next = !isOpen;
    setIsOpen(next);

    if (next) {
      onOpen?.();
      scrollIntoViewIfNeeded();
      setHighlightedIndex(0);
    } else {
      setSearch("");
      setHighlightedIndex(0);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        closeDropdown();
      }
    };

    setTimeout(() => {
      document.addEventListener("mousedown", handler);
    }, 0);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (menuRef.current && buttonRef.current) {
        const pos = getMenuPosition();
        Object.assign(menuRef.current.style, pos);
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, zIndex]);

  const handleSelect = (opt: Option) => {
    if (multiple) {
      const arr = Array.isArray(value) ? [...value] : [];
      const optValue = opt.value ?? "";
      const idx = arr.indexOf(optValue);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(optValue);
      onChange(arr);
    } else {
      onChange(opt.value);
      closeDropdown();
    }
  };

  const displayValue = multiple
    ? Array.isArray(value)
      ? options.filter(o => value.includes(o.value ?? "")).map(o => o.label).join(", ") || t('selectPlaceholder')
      : t('selectPlaceholder')
    : options.find(o => o.value === value)?.label || t('selectPlaceholder');

  const filtered = search
    ? options.filter(o =>
        o.label?.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  const handleButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) {
      onKeyDown?.(e);
      return;
    }
    
    if (e.key === "Tab" || e.key === "Escape") {
      onKeyDown?.(e);
      return;
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev < filtered.length - 1 ? prev + 1 : prev
      );
      
      setTimeout(() => {
        const items = menuRef.current?.querySelectorAll(`.${styles.item}`);
        const highlighted = items?.[highlightedIndex + 1] as HTMLElement;
        highlighted?.scrollIntoView({ block: 'nearest' });
      }, 0);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
      
      setTimeout(() => {
        const items = menuRef.current?.querySelectorAll(`.${styles.item}`);
        const highlighted = items?.[highlightedIndex - 1] as HTMLElement;
        highlighted?.scrollIntoView({ block: 'nearest' });
      }, 0);
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      
      if (filtered.length > 0) {
        handleSelect(filtered[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closeDropdown();
    } else if (e.key === "Tab") {
      closeDropdown();
      onKeyDown?.(e);
    }
  };

  return (
    <>
      <div className={styles.wrapper}>
        {label && <label className={styles.label}>{label}</label>}

        <button
          ref={buttonRef}
          type="button"
          className={`${styles.button} ${isOpen ? styles.buttonOpen : ''} ${disabled ? styles.buttonDisabled : ''}`}
          onClick={toggleOpen}
          onKeyDown={handleButtonKeyDown}
          onFocus={() => {
            if (autoOpen && !disabled && !isOpen) {
              setTimeout(() => {
                if (document.activeElement === buttonRef.current && !isOpen) {
                  toggleOpen();
                }
              }, 100);
            }
          }}
          disabled={disabled}
          style={isOpen ? { zIndex: zIndex - 1 } : {}}
        >
          {icon && <FontAwesomeIcon icon={icon} className={styles.leftIcon} />}
          <span className={styles.text}>{displayValue}</span>
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className={styles.arrow}
          />
        </button>
      </div>

      {isOpen && createPortal(
        <div 
          ref={menuRef}
          className={styles.menu}
          style={getMenuPosition()}
        >
          <div className={styles.searchWrapper}>
            <input
              ref={searchInputRef}
              className={styles.searchInput}
              placeholder={t('search')}
              value={search}
              onChange={(e) => {
                const text = e.target.value;
                setSearch(text);
                onSearch?.(text);
              }}
              onKeyDown={handleSearchKeyDown}
            />
          </div>

          <div className={styles.items}>
            {filtered.length === 0 && (
              <div className={styles.noResults}>No results</div>
            )}

            {filtered.map((opt, idx) => {
              const optValue = opt.value ?? "";
              const selected = multiple
                ? Array.isArray(value) && value.includes(optValue)
                : value === optValue;
              const highlighted = idx === highlightedIndex;

              return (
                <div
                  key={optValue || `option-${idx}`}
                  className={`${styles.item} ${selected ? styles.selected : ""} ${highlighted ? styles.highlighted : ""}`}
                  onClick={() => handleSelect(opt)}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                >
                  <span>{opt.label}</span>
                  {selected && <FontAwesomeIcon icon={faCheck} className={styles.check} />}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}